import React, { useRef, useCallback, useState } from 'react';
import { LogOut, Image as ImageIcon, User, Shield, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { Logo } from '../components/Logo';
import MusicServicesDialog from '../components/MusicServicesDialog';
import EditProfileDialog from '../components/EditProfileDialog';
import PrivacyPolicy from '../components/PrivacyPolicy';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

function Settings() {
  const navigate = useNavigate();
  const { user, updateProfile } = useUser();
  const [showMusicServices, setShowMusicServices] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || deleting) return;

    setDeleting(true);
    try {
      // Get the session token before signing out
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('No valid session');
      }

      // Sign out first
      await supabase.auth.signOut();

      // Delete user profile (trigger will handle cascading deletes)
      const { error: profileError } = await supabase
        .from('users')
        .delete()
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Call edge function to delete auth user
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete user account');
      }

      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'cover') => {
    if (!user || !updateProfile) return;
    
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }

      setUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${type === 'avatar' ? 'avatars' : 'covers'}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user-content')
        .upload(filePath, file, {
          upsert: true,
          cacheControl: '3600',
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('user-content')
        .getPublicUrl(filePath);

      if (type === 'avatar') {
        await updateProfile({ avatarUrl: publicUrl });
      } else {
        await updateProfile({ coverImage: publicUrl });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [user, updateProfile]);

  const handleProfileUpdate = async (data: { username: string; displayName: string; bio: string }) => {
    if (!updateProfile) return;
    
    try {
      await updateProfile({
        username: data.username,
        displayName: data.displayName,
        bio: data.bio,
      });
      setShowEditProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  if (!user) return null;

  if (showPrivacyPolicy) {
    return <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8 text-indigo-600" />
            <span className="text-lg font-bold text-indigo-600">Settings</span>
          </div>
        </div>
      </header>

      <div className="mobile-main pt-2">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="space-y-6">
              {/* Account Settings */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Account</h2>
                <div className="space-y-4">
                  {/* Profile Picture */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden">
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          ref={avatarInputRef}
                          onChange={(e) => handleFileUpload(e, 'avatar')}
                          accept="image/*"
                          className="hidden"
                        />
                      </div>
                      <div>
                        <h3 className="font-medium">Profile Picture</h3>
                        <p className="text-sm text-gray-600">Update your profile photo</p>
                      </div>
                    </div>
                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploading}
                      className={`px-4 py-2 bg-white border border-gray-300 rounded-lg transition-colors ${
                        uploading 
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {uploading ? 'Uploading...' : 'Change'}
                    </button>
                  </div>

                  {/* Cover Photo */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="w-24 h-16 rounded-lg bg-gray-200 overflow-hidden">
                          {user.coverImage ? (
                            <img
                              src={user.coverImage}
                              alt="Cover"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          ref={coverInputRef}
                          onChange={(e) => handleFileUpload(e, 'cover')}
                          accept="image/*"
                          className="hidden"
                        />
                      </div>
                      <div>
                        <h3 className="font-medium">Cover Photo</h3>
                        <p className="text-sm text-gray-600">Update your profile cover</p>
                      </div>
                    </div>
                    <button
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploading}
                      className={`px-4 py-2 bg-white border border-gray-300 rounded-lg transition-colors ${
                        uploading 
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {uploading ? 'Uploading...' : 'Change'}
                    </button>
                  </div>

                  {/* Profile Information */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium">Profile Information</h3>
                      <p className="text-sm text-gray-600">Update your profile details</p>
                    </div>
                    <button
                      onClick={() => setShowEditProfile(true)}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>

              {/* Music Services */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Music Services</h2>
                <div className="space-y-3">
                  <button 
                    onClick={() => setShowMusicServices(true)}
                    className="w-full py-3 px-4 rounded-lg flex items-center justify-between bg-green-500 text-white hover:bg-green-600 transition-colors"
                  >
                    Connect Spotify
                  </button>

                  <button
                    onClick={() => setShowMusicServices(true)}
                    className="w-full py-3 px-4 rounded-lg flex items-center justify-between bg-pink-500 text-white hover:bg-pink-600 transition-colors"
                  >
                    Connect Apple Music
                  </button>
                </div>
              </div>

              {/* Privacy & Legal */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Privacy & Legal</h2>
                <button 
                  onClick={() => setShowPrivacyPolicy(true)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="flex items-center">
                    <Shield className="h-5 w-5 mr-3 text-indigo-600" />
                    <span>Privacy Policy</span>
                  </span>
                </button>
              </div>

              {/* Account Actions */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Account Actions</h2>
                <div className="space-y-3">
                  <button 
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-between px-4 py-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-red-600"
                  >
                    <span className="flex items-center">
                      <LogOut className="h-5 w-5 mr-3" />
                      <span>Sign Out</span>
                    </span>
                  </button>

                  <button 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-red-100 rounded-lg hover:bg-red-200 transition-colors text-red-700"
                  >
                    <span className="flex items-center">
                      <Trash2 className="h-5 w-5 mr-3" />
                      <span>Delete Account</span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <MusicServicesDialog
            isOpen={showMusicServices}
            onClose={() => setShowMusicServices(false)}
          />

          <EditProfileDialog
            isOpen={showEditProfile}
            onClose={() => setShowEditProfile(false)}
            initialData={user}
            onSave={handleProfileUpdate}
          />

          <DeleteConfirmDialog
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDeleteAccount}
            deleting={deleting}
            title="Delete Account"
            message="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted."
          />
        </div>
      </div>
    </div>
  );
}

export default Settings;